import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature, MetricLibraryGridType } from 'webapp/enums/enums';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricLibraryGridItemComponent, {
	Size as ItemSize,
} from 'webapp/components/metric-library/grid-item';
import MetricLibraryGridComponent from 'webapp/components/metric-library/grid/index';
import type { Args as GridBaseArgs } from 'webapp/components/metric-library/grid/index';

import type { Metric, Source, SourceType } from 'webapp/models-custom';
import type BatchSelect from 'webapp/models-custom/batch-select';
import type FilterSortMetricLibraryMainMetrics from 'webapp/models-custom/filter-sort/metric-library-main-metrics';
import type FilterSortMetricLibrarySourceMetrics from 'webapp/models-custom/filter-sort/metric-library-source-metrics';

interface Args extends Omit<GridBaseArgs, 'filterSort'> {
	isShown: boolean;
	filterSort: FilterSortMetricLibraryMainMetrics | FilterSortMetricLibrarySourceMetrics;
	type?: MetricLibraryGridType;
	source?: Source | null;
	sourceType?: SourceType;
	batchSelect: BatchSelect<Metric>;
	onConnectMetric?: () => void;
}

export default class MetricLibraryGridMetricsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	visibleCheckContainerNode: HTMLElement | null = null;

	get itemSize() {
		return this.isTypeBasic ? ItemSize.NORMAL : ItemSize.SMALL;
	}

	get sourceType() {
		return this.args.sourceType;
	}

	get isTypeBasic() {
		return this.args.type === MetricLibraryGridType.BASIC;
	}

	get isTypeCustom() {
		return this.args.type === MetricLibraryGridType.CUSTOM;
	}

	get canAddUserPushed() {
		return (
			(this.sourceType?.isToken && this.billingService.dataPushEnabled) ||
			this.sourceType?.isTokenWrapper
		);
	}

	get isAddButtonShown() {
		if (this.sourceType?.isLegacyCustomSourceType && !this.args.source?.isDataset) {
			if (this.sourceType.isSQLType) {
				return this.billingService.checkIfCan(BillingFeature.SQL_LEGACY_METRICS);
			}

			if (this.sourceType.isSheets) {
				return this.billingService.checkIfCan(BillingFeature.GSHEETS_LEGACY_METRICS);
			}

			return false;
		}

		return this.isTypeCustom || (this.isTypeBasic && this.canAddUserPushed && this.args.source);
	}

	get isSuggestMetricButtonShown() {
		return this.sourceType?.id && this.sourceType.hasBasicMetrics && this.isTypeBasic;
	}

	get initialReducedCount() {
		return Number(this.isAddButtonShown) + Number(this.isSuggestMetricButtonShown);
	}

	get classNames() {
		const isShown = this.args.isShown ?? true;
		let classNames = this.args.classNames;

		if (!isShown) {
			classNames += ' hidden';
		}

		return classNames;
	}

	@action
	suggestNewMetric() {
		this.mmMetricsService.openMetricRequestForm();
	}

	@action
	addNewMetric() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.isTypeCustom) {
			this.mmMetricsService.openCreateNewCustomMetricPopup('metrics');
		} else if (this.isTypeBasic) {
			// only for custom metrics
			const source = this.args.source;
			if (source) {
				const spaceAccessId = source.numericId!;
				this.mmMetricsService.createNewCustomPushMetric(spaceAccessId);
			}
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}
	<template>
		<MetricLibraryGridComponent
			@filterSort={{@filterSort}}
			@title={{@title}}
			@type={{@type}}
			@initialRowCount={{@initialRowCount}}
			@initialReducedCount={{this.initialReducedCount}}
			@loadMoreRowCount={{@loadMoreRowCount}}
			@classNames={{this.classNames}}
			as |visibleItems settings|
		>
			<ul
				class="flex-ct flex-ct--size-block-metric"
				{{didInsert this.setupView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |model|}}
					<MetricLibraryGridItemComponent
						@model={{model}}
						@size={{this.itemSize}}
						@canSelect={{model.isOnSource}}
						@batchSelect={{@batchSelect}}
						@onConnectMetric={{@onConnectMetric}}
					/>
				{{/each}}

				{{#if this.isAddButtonShown}}
					<li
						role="button"
						class="card card--{{this.itemSize}}-metric-type card--new flex-item filter-all"
						{{on "click" this.addNewMetric}}
					>
						<CommonUiIconComponent @name="Plus" @size="sm" />
					</li>
				{{/if}}

				{{#if this.isSuggestMetricButtonShown}}
					<li
						role="button"
						class="card card--{{this.itemSize}}-metric-type card--suggest card--no-hover flex-item filter-all"
						{{on "click" this.suggestNewMetric}}
					>
						<div class="card__wrapper">
							<div class="card__content">
								<div class="card__header">
									<CommonUiIconComponent
										@name="DatablockAdd"
										@size="sm"
										@class="card__icon"
									/>
									<span class="card__metric_name">Suggest a new metric</span>
								</div>
								{{#if @sourceType}}
									<div class="card__description">
										Is there a
										{{@sourceType.name}}
										metric you are missing here?
									</div>
								{{/if}}
							</div>
						</div>
					</li>
				{{/if}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=settings.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>
		</MetricLibraryGridComponent>
	</template>
}
